import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy } from "@angular/core";
import { AuthService, ClientIdService, ClientSideCacheService, DialogService, GlobalConfigService, LoaderService, LocalizationService, LogService, MibpLogger, PermissionService, SignalRService, UrlHelperService, neverEndingPromise } from "root/services";
import { filter, firstValueFrom, forkJoin, take } from "rxjs";
import { DialogComponent } from "root/shared/components/dialogs/dialog/dialog.component";
import { MibpSessionService } from "root/services/mibp-session/mibp-session.service";
import { Router, RouterOutlet } from '@angular/router';
import { MibpSessionViewModel, UserStatus, UserType } from 'root/mibp-openapi-gen/models';
import { ApplicationStateService } from "root/services/application-state/application-state.service";
import { DocumotoAuthService } from "root/services/auth-service/documoto-auth.service";
import { DialogButton } from "root/shared/components/dialogs/dialog/dialog.types";
import { DialogButtonType } from "root/shared/components/dialogs/dialog/dialog-button.enum";
import { Guid } from "guid-typescript";
import { MibpActiveAcountPickerComponent } from "../active-account-picker/active-account-picker.component";

import { DeliverySequencePickerComponent } from 'root/components/deliverysequence-picker/deliverysequence-picker.component';
import { ResourceStringComponent } from 'root/shared/components/localization/resource-string/resource-string.component';
import { ContactUsService } from 'root/services/contact-us/contact-us.service';
import { MySandvikFeatures, permissionPolicyKey } from 'root/services/permission';
import { environment } from "root/environment";
import { usersFnEnsureActiveDeliverySequence } from "root/mibp-openapi-gen/fn/users/users-fn-ensure-active-delivery-sequence";
import { usersFnUpdateDefaultDeliverySequence } from "root/mibp-openapi-gen/fn/users/users-fn-update-default-delivery-sequence";
import { MibpApiService } from "root/mibp-openapi-gen/mibp-api.service";
import { MibpInlineHelpComponent } from 'root/components/inline-help/inline-help.component';

/**
 * This component wraps the main router outlet
 * It will hide the main router outlet until showApplication == true
 */
@Component({
  selector: 'mibp-app-loader',
  templateUrl: './app-loader.component.html',
  styleUrls: ['./app-loader.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MibpActiveAcountPickerComponent, DialogComponent, DeliverySequencePickerComponent, ResourceStringComponent, MibpInlineHelpComponent, RouterOutlet]
})
export class MibpAppLoaderComponent implements OnInit {
  private mibpApiService = inject(MibpApiService);
  private localization = inject(LocalizationService);
  private stateService = inject(ApplicationStateService);
  private auth = inject(AuthService);
  private urlHelper = inject(UrlHelperService);
  private router = inject(Router);
  private cache = inject(ClientSideCacheService);
  private documotoAuth = inject(DocumotoAuthService);
  private clientIdService = inject(ClientIdService);
  private signalrService = inject(SignalRService);
  session = inject(MibpSessionService);
  loaderService = inject(LoaderService);
  private globalConfig = inject(GlobalConfigService);
  private permissionService = inject(PermissionService);
  private dialogService = inject(DialogService);
  private contactUsService = inject(ContactUsService);


  /**
   * Set to true to show the main router outlet and let the application load
   */
  protected showApplication = false;

  /**
   * Hide/Show the main application loader
   * This will look like the loader in index.html and use the same CSS to make the loading experience "non jumpy"
   */
  protected showLoader = false;

  protected resourcesAreLoaded = false;

  protected translations = {
    'general-error-title': { value: null, resourceKey: 'General_Error_InlineTitle', fallbackText: 'Whoops! Something has gone wrong' },
    'load-session': { value: null, resourceKey: 'x', fallbackText: 'Could not load your account information' },
    'load-resources': { value: null, resourceKey: 'x', fallbackText: 'Could not load translations' },
    'what-to-do-now': { value: null, resourceKey: 'Global_Error_WhatToDoNow', fallbackText: 'Try the following to see if the problem resolves itself' },
    'reload-page': { value: null, resourceKey: 'Global_Error_ReloadSuggestion', fallbackText: 'Reload the page to try again' },
    'try-later': { value: null, resourceKey: 'Global_Error_TryLaterSuggestion', fallbackText: 'Wait and try later' },
    'login-again': { value: null, resourceKey: 'AppLoad_ClearCacheAndReload', fallbackText: '[Clear cache] and reload' },
    'contact-support': { value: null, resourceKey: 'Global_Error_ContactSupport', fallbackText: 'Contact support if the problem remains' }
  };


  /**
   * The name of the portal
   */
  protected portalNameText = 'My Sandvik Customer Portal';
  protected showActAsPrompt = false;
  protected name: string;
  protected clientId: string;
  protected error?: 'load-session' | 'load-resources';
  protected clearCacheAndreloadHtml: string;

  protected log: MibpLogger;
  dialogButtons: DialogButton[] = [
    { resourceKey: 'Global_Save', id: 'save', disabled: true, type: DialogButtonType.Primary },
    { resourceKey: 'Global_ContactUs', id: 'contact_us', type: DialogButtonType.Primary }
  ];
  concurrenSessionDialogButtons: DialogButton[] = [
    { resourceKey: 'Users_ConcurrentSession_LogOutButton', id: 'logout', type: DialogButtonType.Danger},
    { resourceKey: 'Users_ConcurrentSession_StayLoggedInButton', id: 'stayloggedin', type: DialogButtonType.Primary}
  ];
  @ViewChild('actAsDialog') actAsDialog: DialogComponent;
  @ViewChild('concurrenSessionDialog') concurrenSessionDialog: DialogComponent;
  @ViewChild(MibpActiveAcountPickerComponent) activeAccountPicker: MibpActiveAcountPickerComponent;
  private resolveOpenPromise;
  private resolveConcurrentSessionPromise;
  protected currentOrganizationId: Guid;
  private deliverySequenceId: number;
  public message: string;
  public canActAs = false;
  protected showContactUsFallback = false;
  private pendingContactUsMessage?: string;

  constructor() {
    const logger = inject(LogService);
    this.log = logger.withPrefix(`app-loader`);
  }

  /**
   * Check if backend/frontend custom domain settings mismatch, and if so, show a warning dialog.
   * This is to prevent confusion and potential issues with authentication due to mismatching settings.
   */
  private async guardB2cConfiguration(): Promise<void> {
    if (this.globalConfig.b2CCustomDomainEnabled !== environment.auth.b2c.customDomain.enabled) {
      this.showLoader = false;
      await this.dialogService.promptWithText(`B2C Domain Config is ${this.boolToEnabledDisabled(this.globalConfig.b2CCustomDomainEnabled)} in backend but ${this.boolToEnabledDisabled(environment.auth.b2c.customDomain.enabled)} in frontend environment config. This might cause issues with authentication.`, 
        {
          id  : 'ok',
          resourceKey: null,
          type: DialogButtonType.Danger,
          textOverride: 'I understand'
        }
      );
      this.showLoader = true;
    }
  }

  
  private boolToEnabledDisabled(value: boolean): string {
    return value ? 'Enabled' : 'Disabled';
  }
  

  /**
   * When an error has occured and user wants to try to clear cache.
   */
  clearCacheClick(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
    if ((e.target as HTMLElement).nodeName === 'A') {
      this.stateService.clearAllCacheAndReload();
    }
  }

  /**
   * Method to prepare texts
   */
  private tryGetTranslatedTexts(): void {
    this.translations = this.stateService.getTranslationsForApplicationLoad();

  }

  ngOnInit(): void {

    void this.initializeComponent();

  }


  private async initializeComponent(): Promise<void> {
    this.clientId = this.clientIdService.getClientId();

    // Language redirect
    const lang = this.localization.verifyLanguageFromUrl(window.location.href);
    if (typeof lang === 'string') {
      window.location.href = lang;
      return;
    }
    this.showLoader = true;

    this.tryGetTranslatedTexts();

    // Make sure frontend/backend B2C Custom domain config match
    await this.guardB2cConfiguration();

    // Start by loading session data so we don't get duplicate connections!
    await this.loadSessionData();

    const localizationObservable = this.localization.loadOnApplicationStart().pipe(filter(f => f != null), take(1));

    localizationObservable.subscribe({
      next: () => {
        this.resourcesAreLoaded = true;
        this.tryGetTranslatedTexts();
      },
      error: () => {
        this.error = 'load-resources';
      }
    });

    forkJoin([localizationObservable]).subscribe(
      {
        next: async () => {
          this.tryGetTranslatedTexts();
          this.resourcesAreLoaded = true;

          // Hide the loader
          this.showLoader = false;

          // Check user status and redirect accordingly
          await this.redirectNonActiveUsers();

          await this.detectAccountInconsistencies();

          if (this.session.hasActiveUser()) {
            await this.showChangeAccountDialogForInactiveDeliverySequence();
          }

          // Check if we need to redirect user to profile picker
          await this.checkPunchoutProfile();

          await this.actAsCorrectPunchoutCxmlDeliverySequence();

          this.showApplication = true;
          this.openPendingContactUsDialog();
          this.connectToSignalr();
          this.documotoAuth.ensureSignedout();
        },
        error: (error) => {
          this.tryGetTranslatedTexts();
          this.log.error(`AppLoad error`, error);
        }
      });
  }

  /**
   * When login for cxml users we receive an encoded 'ds' querystring parameter
   * We will here attempt to act as that encoded delivery sequence.
   * If it fails, the user will see a message and an option to sign out
   */
  private async actAsCorrectPunchoutCxmlDeliverySequence(): Promise<void> {
    const encodeddeliverySequence = this.urlHelper.getQuerystringValue('ds');
    if (encodeddeliverySequence && this.session.current?.user?.isPunchoutUser) {
      try {
        await this.session.actAsPunchoutEncodedDeliverySequence(encodeddeliverySequence);
      } catch {

        // Could not act as the delivery sequence in the Address configuration
        // We must not let the user continue.

        // Show a message with a signout button
        this.dialogService.promptWithHtml('Punchout_CxmlActAsError', 'Global_Logout').finally(() => {
          this.auth.signout();
        });

        // But we do not wait for user to click sign out. We clear what we can at once.
        // Otherwise user will be logged if they manually reload the page
        this.cache.clearEverything();
        this.auth.clearAll();
        this.clientIdService.newClientId();

        // Never resolve this promise - we should should never continue to load the application
        await neverEndingPromise();
      }
    }
  }

  private connectToSignalr(): void {
    if (this.session.isLoggedIn()) {
      setTimeout(() => {
        this.signalrService.connect();
      }, 1500);
    }
  }

  private async redirectNonActiveUsers(): Promise<void> {
    if (this.session?.isEmailVerificationRequired()) {
      if (this.router.url.indexOf('/user/verify-email') == -1 && this.router.url.indexOf('/support/contact') == -1) {
        this.router.navigate([this.localization.getLang(), 'user', 'verify-email']);
      }
    } else if (this.session?.isRegistrationRequired() && this.router.url.indexOf('/user/newuserregistration') == -1) {
      this.router.navigate([this.localization.getLang(), 'user', 'newuserregistration']);
    } else if (this.session?.current?.user?.status == UserStatus.New && this.router.url.indexOf('/user/new') == -1) {
      this.router.navigate([this.localization.getLang(), 'user', 'new']);
    }else if(this.session?.hasUser() && this.session?.isUserDeactivated()){
      this.router.navigate([this.localization.getLang(), 'user', 'deactivated']);
    }
  }

  delay(ms: number): Promise<void> {
    return new Promise<void>(resolve => setTimeout(resolve, ms));
  }


  private async checkPunchoutProfile(): Promise<void> {

    if (this.session.userMustPickPunchoutProfile() && !this.router.url.includes('profilepicker')) {
      this.router.navigate([this.localization.getLang(), 'user', 'profilepicker']);
    }

  }

  /**
   * If we have a B2C user - then we try to load session data
   */
  private async loadSessionData(): Promise<MibpSessionViewModel> {

    const b2cUser = this.auth.getB2CUser();

    if (b2cUser) {
      this.log.info(`B2CUser:`, `${b2cUser.email} (${b2cUser.type}). IdentityObjectId ${b2cUser.identityObjectId}. Nonce (ClientId) ${b2cUser.nonce}. Token expires ${b2cUser?.exp}`);
      this.cache.setUserKey(b2cUser.identityObjectId);
      try {
        const sessionData = await this.session.loadAtApplicationStart(b2cUser);
        this.log.info(`SessionData`, sessionData);
        if(this.session.current?.user?.isPunchoutUser == false){
          await this.detectConcurrentSessions().then((isConcurrentSession) => {
            if(isConcurrentSession){
              return new Promise((resolve) => {
                setTimeout(() => {
                  this.showLoader = false;
                  this.resolveConcurrentSessionPromise = resolve;
                  this.concurrenSessionDialog.open();
                }, 1000);
              });
            }
          });
        }

        return sessionData.sessionData;
      } catch (e) {
        this.log.error("FAIL LOADING USER DATA", e);
        this.error = 'load-session';
        throw e;
      }
    } else {
      this.log.debug(`B2CUser:`, null);
    }

    return null;
  }

  private detectConcurrentSessions(): Promise<boolean> {
    return this.auth.detectConcurrentUserSessions();
  }

  private async detectAccountInconsistencies(): Promise<void> {

    // Detect if user is active without organization?! Should be rare, but at least we detect it
    if (this.session.current?.user?.status == UserStatus.Active && !this.session.current?.user?.accessGroupId) {
      await this.dialogService.promptWithText("Data inconsistency discovered - user is Active but have no Organization. Please contact support.", {id: 'logout', resourceKey: 'Global_Logout'});
      this.auth.signout();
      return neverEndingPromise();
    }
  }

  /**
   * If Active Account is inactivated then user must choose another one.
   * @returns
   */
  private async showChangeAccountDialogForInactiveDeliverySequence(): Promise<void> {
    if (!this.session.current?.user || this.session?.current?.user?.isPunchoutUser) {
      return;
    }
       
    const isDeliverySequenceActive = this.session?.current?.activeDeliverySequence?.isActive;
    const isBusinessRelationActive = this.session?.current?.activeDeliverySequence?.isBusinessRelationActive;
    const isCompanyActive = this.session?.current?.activeDeliverySequence?.isCompanyActive;
    const isActive = isDeliverySequenceActive && isBusinessRelationActive && isCompanyActive;

    if (this.session?.current?.activeDeliverySequence?.deliverySequenceId == null) {
      await this.activeAccountPicker.open({
        forcedChangeReason: 'no-active-delivery-sequence'
      });
      return;
    }

    if (isActive) {
      return;
    }

    await this.activeAccountPicker.open({
      forcedChangeReason: 'active-account-inactive'
    });       
  }

  //TODO: MIBP-628 CLEANUP
  private async displayContactUsForInactiveDeliverySequence(): Promise<void> {



    if (this.session.current?.user?.type == UserType.External && this.session.current?.user?.status == UserStatus.Active
      && !this.session?.current?.user?.isPunchoutUser) {//skip check for punchout user
      this.localization.getResourceString("Login_InactiveDeliverySequence_Message").then(r => {
        const deliverySequence = this.session.current?.user?.defaultDeliverySequence;
        let replaceString = "";
        if (deliverySequence) {
          replaceString = "\"" + deliverySequence.companyCode + "/" + deliverySequence.erpCustomerId + "/" + deliverySequence.deliverySequenceNumber + "\"";
        }
        this.message = r.replace('{deliverySequence}', replaceString);
      });

      this.canActAs = this.permissionService.test(permissionPolicyKey('canActAs'));
      if (!this.canActAs) {
        this.dialogButtons = this.dialogButtons.filter((b) => { return b.resourceKey == 'Global_ContactUs'; });
      }
      const hasActiveDeliverySeq = await this.ensureActiveDeliverySequence();

      if (!hasActiveDeliverySeq) {
        this.openContactUsOrFallback();
      }
    }
    else {
      window.localStorage.removeItem('mibp.actasdialog');
    }
  }

  private async ensureActiveDeliverySequence(): Promise<boolean> {
    this.currentOrganizationId = Guid.parse(this.session?.current?.user?.accessGroupId);
    return new Promise((resolve) => {
      this.resolveOpenPromise = resolve;
      if (window.localStorage.getItem('mibp.actasdialog')) {
        if (window.localStorage.getItem('mibp.actasdialog') == 'false') {
          this.showActAsPrompt = false;
          resolve(true);
        } else {
          this.showActAsPrompt = true;
        }

        if (this.showActAsPrompt && this.session.current?.user) {
          this.name = this.session.current?.user?.firstName;
          this.actAsDialog.open();
        }
      }
      else {
        firstValueFrom(this.mibpApiService.invoke(usersFnEnsureActiveDeliverySequence)).then((userDelSeq) => {
          if (userDelSeq.isActiveDefaultDeliverySequence) {
            window.localStorage.setItem('mibp.actasdialog', 'false');
            this.showActAsPrompt = false;
            resolve(true);
          }
          else {
            window.localStorage.setItem('mibp.actasdialog', 'true');
            this.showActAsPrompt = true;
          }

          if (this.showActAsPrompt && this.session.current?.user) {
            this.name = this.session.current?.user?.firstName;
            this.actAsDialog.open();
          }
        }, (ex) => {
          this.log.error(ex.error.error);
          this.dialogService.prompt(`UserNotLoggedInInfo`, 'Global_Dialog_Close').then(() => {
            this.auth.signout();
          });
        });
      }
    });
  }

  async dialogClick(btn: DialogButton): Promise<void> {
    if (btn.id === 'save') {
      await firstValueFrom(this.mibpApiService.invoke(usersFnUpdateDefaultDeliverySequence, { deliverySequenceId: this.deliverySequenceId })).then(() => {
        this.dialogClosed(true);
        this.session.actAs(this.deliverySequenceId);
        window.localStorage.setItem('mibp.actasdialog', 'false');
      }).catch(error => {
        this.log.error('Error when updating user default delivery sequence', error);
      });
    }
    else if (btn.id === 'close') {
      this.dialogClosed(false);
    }
    else if (btn.id === 'contact_us') {
      if (this.showContactUsFallback) {
        this.dialogClosed(false);
        return;
      }

      if (!this.session.hasFeature(MySandvikFeatures.ContactUsForm)) {
        this.showContactUsFallbackDialog();
        return;
      }

      this.dialogClosed(false);
    }
  }

  private openContactUsOrFallback(): void {
    if (this.session.hasFeature(MySandvikFeatures.ContactUsForm)) {
      if (!this.showApplication) {
        this.pendingContactUsMessage = this.message;
        return;
      }

      this.contactUsService.openGeneralContactUs(this.message);
      return;
    }

    this.showContactUsFallback = true;
    this.showActAsPrompt = true;
    this.dialogButtons = [
      { resourceKey: 'Global_Dialog_Close', id: 'close', type: DialogButtonType.Primary }
    ];
    this.actAsDialog.open();
  }

  private openPendingContactUsDialog(): void {
    if (!this.pendingContactUsMessage) {
      return;
    }

    const message = this.pendingContactUsMessage;
    this.pendingContactUsMessage = undefined;
    setTimeout(() => this.contactUsService.openGeneralContactUs(message));
  }

  private showContactUsFallbackDialog(): void {
    this.showContactUsFallback = true;
    this.dialogButtons = [
      { resourceKey: 'Global_Dialog_Close', id: 'close', type: DialogButtonType.Primary }
    ];
  }

  async concurrenSessionDialogClick(btn: DialogButton): Promise<void> {
    if(btn.id == "logout"){
      this.auth.signoutAllSignIns();
    }
    else{
      this.concurrenSessionDialog.close();
      this.showLoader = true;
      this.log.info(`User continues with concurrent user session login`, this.session?.current?.user?.userId);
      return this.resolveConcurrentSessionPromise(true);
    }


  }

  async dialogClosed(isResolved: boolean): Promise<void> {
    this.actAsDialog.close();
    return this.resolveOpenPromise(isResolved);
  }

  onDeliverySequenceChanged(value): void {
    this.dialogButtons[0].disabled = true;
    if (!value || value.active == false) {
      return;
    }
    this.deliverySequenceId = value.id;
    this.dialogButtons[0].disabled = false;
  }
}
